'use client';

import { createContext } from 'react';

import type {
  PopStoreExtendedSubscriptionLevel,
  PopStoreSubscriptionLevel,
} from '@ps/models/lib/creator-subscription';

/**
 * The signed-in viewer's access relationship with the ONE creator whose bio is
 * on screen. Unlike PopshopRN's `PopStoreUserAccessContext` — a pure SPA that
 * cached every creator relationship in a `Map` — a bio page is always about a
 * single creator, so this context tracks exactly that one relationship and
 * nothing more (no lead-form / contact-form / guest state).
 *
 * This is per-viewer, resolved client-side (needs the Firebase session), so it
 * MUST NOT live in cached HTML — consumers belong inside a `<Suspense>` island.
 */
export interface BioUserAccessContextValue {
  /** The signed-in viewer. `undefined` while auth resolves; `null` once logged out. */
  currentUserId: string | null | undefined;

  /** The creator this relationship is about (the bio's `profile.id`). */
  creatorId: string;

  /**
   * How the viewer follows or subscribes to this creator, as one level: a paid
   * tier wins, a bare follower is `FOLLOWER`, and a stranger (or a viewer still
   * resolving) is `undefined`. Built from the *access* level, so a
   * canceled-but-still-valid subscription still reads at its paid tier.
   */
  extendedSubscriptionLevel: PopStoreExtendedSubscriptionLevel | undefined;

  /**
   * The tier the viewer is *actively* paying for — `undefined` once the
   * subscription is canceled (even while it's still valid). Use it to tell
   * "Subscribe" from "Upgrade"; use {@link extendedSubscriptionLevel} to gate
   * content access.
   */
  activeSubscriptionLevel: PopStoreSubscriptionLevel | undefined;

  /** Whether the viewer follows this creator; `undefined` while resolving. */
  isFollowing: boolean | undefined;

  /** `true` until the relationship has been resolved. */
  loading: boolean;

  /** Re-read the whole relationship (subscription + follow) — call after a subscribe action. */
  refresh: () => void;

  /**
   * Imperatively set the follow status — e.g. optimistically after a
   * follow/unfollow, when the realtime subscription is off (the default). With
   * the realtime subscription on, the live listener will re-assert the truth.
   */
  updateFollowingStatus: (following: boolean) => void;

  /**
   * Re-check the follow status via the server. The imperative counterpart to the
   * realtime subscription: call it after a follow/unfollow (or on demand) when
   * `realtimeFollowingStatusEnabled` is off.
   */
  refreshFollowingStatus: () => void;
}

/**
 * Stranger sentinel. Returned by `useBioUserAccess` when no provider is mounted
 * (e.g. an editor preview) and used as the provider's pre-resolution value, so
 * consumers never have to defend against `undefined`.
 */
export const NO_BIO_USER_ACCESS: BioUserAccessContextValue = {
  currentUserId: undefined,
  creatorId: '',
  extendedSubscriptionLevel: undefined,
  activeSubscriptionLevel: undefined,
  isFollowing: undefined,
  loading: false,
  refresh: () => {},
  updateFollowingStatus: () => {},
  refreshFollowingStatus: () => {},
};

export const BioUserAccessContext =
  createContext<BioUserAccessContextValue>(NO_BIO_USER_ACCESS);
